import { RefinementCtx, z, ZodError } from 'zod';
import { Question, QuestionSchema } from './question';
import {
  Answer,
  AnswerSchema,
  isDocumentAnswer,
  isVoiceAnswer,
  SubmitAnswers,
} from './answers';

export const validateQuestionAnswer = (
  { question, answer }: { question: Question; answer: Answer },
  ctx: RefinementCtx,
) => {
  switch (question.parameters.type) {
    case 'text':
      if (typeof answer !== 'string') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Answer must be a string',
        });
      }
      break;

    case 'radio':
      if (typeof answer !== 'string' && answer !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Answer must be a string or null',
        });
      } else if (typeof answer === 'string') {
        const validLabels = question.parameters.options.map((opt) => opt.label);
        if (!validLabels.includes(answer)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid answer value: ${answer}`,
          });
        }
      }
      break;

    case 'checkboxes':
    case 'select':
      if (!Array.isArray(answer)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Answer must be an array',
        });
      } else {
        const validLabels = question.parameters.options.map((opt) => opt.label);
        const invalidAnswers = answer.filter(
          (ans) => !validLabels.includes(ans),
        );
        if (invalidAnswers.length > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Invalid answer values: ${invalidAnswers.join(', ')}`,
          });
        }

        // Validate that select with allowMultiple false has only one answer
        if (
          question.parameters.type === 'select' &&
          question.parameters.allowMultiple === false &&
          answer.length > 1
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Answer must be a single value',
          });
        }
      }
      break;

    case 'date':
      if (typeof answer !== 'string' && answer !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Answer must be a string or null',
        });
      }
      break;

    case 'number':
      if (typeof answer !== 'number' && answer !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Answer must be a number',
        });
      } else if (typeof answer === 'number') {
        const { min, max, decimalPlaces } = question.parameters;

        if (min !== undefined && answer < min) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Answer must be greater than or equal to ${min}`,
          });
        }

        if (max !== undefined && answer > max) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Answer must be less than or equal to ${max}`,
          });
        }

        // Validate decimal places
        if (decimalPlaces !== undefined) {
          const answerString = answer.toString();
          const decimalIndex = answerString.indexOf('.');

          if (decimalIndex !== -1) {
            const actualDecimalPlaces = answerString.length - decimalIndex - 1;
            if (actualDecimalPlaces > decimalPlaces) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Answer must have at most ${decimalPlaces} decimal places`,
              });
            }
          }
        }
      }
      break;

    case 'document':
      if (!isDocumentAnswer(answer)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Answer must be a document JSON',
        });
      }
      break;

    case 'voice':
      if (!isVoiceAnswer(answer)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Answer must be a voice recording JSON',
        });
      }
      break;
  }
};

export const QuestionAnswerPairSchema = z
  .object({
    question: QuestionSchema,
    answer: AnswerSchema,
  })
  .superRefine(validateQuestionAnswer);

export const QuestionAnswerPairsSchema = z.array(QuestionAnswerPairSchema);

export type QuestionAnswerPair = z.infer<typeof QuestionAnswerPairSchema>;

export function validateSubmittedAnswers(
  submit: SubmitAnswers,
  questions: Question[],
) {
  const byId = new Map(questions.map((q) => [q.id, q]));

  // TODO : doesn't verify all questions that are visible have been answered
  const errors: ZodError[] = [];
  const pairs: QuestionAnswerPair[] = [];
  for (const [questionId, answer] of Object.entries(submit.answers)) {
    const question = byId.get(questionId);
    if (!question) {
      errors.push(
        new ZodError([
          {
            code: z.ZodIssueCode.custom,
            message: `Question ${questionId} not found`,
            path: ['answers', questionId],
          },
        ]),
      );
    } else {
      pairs.push({ question, answer: answer });
    }
  }

  if (errors.length > 0) {
    return {
      success: false,
      error: errors,
    };
  }

  return QuestionAnswerPairsSchema.safeParse(pairs);
}
